const STORAGE_KEY = "dealer-branding";
const DEFAULT_APP_TITLE = "Portal de Gestión";
const DEFAULT_DESCRIPTION = "Sistema de gestión";
const DEFAULT_FAVICON = `${process.env.BASE_URL || "/"}iconadmin.png?v=1`;
const ADMIN_APP_TITLE = "Super Admin";
const ADMIN_DESCRIPTION = "Panel de administración";
const ADMIN_FAVICON = `${process.env.BASE_URL || "/"}iconadmin.png?v=1`;

type BrandingLike = {
  title?: string | null;
  description?: string | null;
  logoUrl?: string | null;
};

type BrandingOptions = {
  isSuperAdmin?: boolean;
};

const clean = (value?: string | null): string =>
  typeof value === "string" ? value.trim() : "";

const hasBrandingData = (branding?: BrandingLike | null): boolean =>
  !!(clean(branding?.title) || clean(branding?.description) || clean(branding?.logoUrl));

export const getStoredBranding = (): BrandingLike | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed ? parsed : null;
  } catch {
    return null;
  }
};

const resolveBranding = (
  branding?: BrandingLike | null,
  options?: BrandingOptions
): BrandingLike | null => {
  if (options?.isSuperAdmin) return null;
  return hasBrandingData(branding) ? branding || null : getStoredBranding();
};

const setMetaContent = (
  selector: string,
  attrs: Record<string, string>,
  content: string
) => {
  let tag = document.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement("meta");
    Object.entries(attrs).forEach(([name, value]) => {
      tag?.setAttribute(name, value);
    });
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const setFavicon = (href: string) => {
  const links = Array.from(
    document.querySelectorAll<HTMLLinkElement>("link[rel*='icon']")
  );
  const iconType = href.includes(".svg") ? "image/svg+xml" : "image/png";

  if (links.length === 0) {
    const link = document.createElement("link");
    link.setAttribute("rel", "icon");
    link.setAttribute("type", iconType);
    link.setAttribute("href", href);
    document.head.appendChild(link);
    return;
  }

  links.forEach((link) => {
    link.setAttribute("type", iconType);
    link.setAttribute("href", href);
  });
};

export const getBrandTitle = (
  branding?: BrandingLike | null,
  options?: BrandingOptions
): string => {
  if (options?.isSuperAdmin) return ADMIN_APP_TITLE;
  return clean(resolveBranding(branding, options)?.title) || DEFAULT_APP_TITLE;
};

export const buildPageTitle = (
  routeTitle: string,
  branding?: BrandingLike | null,
  options?: BrandingOptions
): string => {
  const cleanRouteTitle = clean(routeTitle);
  const appTitle = getBrandTitle(branding, options);
  return cleanRouteTitle ? `${cleanRouteTitle} | ${appTitle}` : appTitle;
};

export const applyPageBranding = (
  routeTitle: string,
  branding?: BrandingLike | null,
  options?: BrandingOptions
) => {
  const resolvedBranding = resolveBranding(branding, options);
  const fullTitle = buildPageTitle(routeTitle, resolvedBranding, options);
  const description = options?.isSuperAdmin
    ? ADMIN_DESCRIPTION
    : clean(resolvedBranding?.description) || DEFAULT_DESCRIPTION;
  const favicon = options?.isSuperAdmin
    ? ADMIN_FAVICON
    : clean(resolvedBranding?.logoUrl) || DEFAULT_FAVICON;

  document.title = fullTitle;
  setMetaContent("meta[name='description']", { name: "description" }, description);
  setMetaContent(
    "meta[property='og:description']",
    { property: "og:description" },
    description
  );
  setMetaContent("meta[property='og:title']", { property: "og:title" }, fullTitle);
  setFavicon(favicon);
};
